import { toast } from 'sonner';
import i18next from 'i18next';

import { useAssignmentTemplateStore } from '@/store/assignment-template.store.js';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class AssignmentTemplateIntegration {
    constructor(service) {
        this.service = service;
    }

    async fetchAll(params = {}) {
        const store = useAssignmentTemplateStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const { columnFilters, ...requestParams } = params;
            const res = await this.service.getAll({
                ...requestParams,
                ...mapColumnFiltersForQuery(columnFilters),
            });
            const list = Array.isArray(res?.data) ? res.data : [];
            const meta = res?.pagination || {};
            store.setItems(list.map((it) => ({ ...it, id: it._id || it.id })));
            store.setPaginationData({
                currentPage: meta.page ?? params.page ?? 1,
                totalPages: meta.totalPages ?? 1,
                totalItems: meta.total ?? list.length,
                itemsPerPage: meta.pageSize ?? params.pageSize ?? 20,
                hasNextPage: false,
                hasPreviousPage: false,
            });
            store.setUniqueValues(res?.uniqueValues || {});
            return { success: true, data: list, uniqueValues: res?.uniqueValues || {} };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchById(id) {
        const store = useAssignmentTemplateStore.getState();
        store.setDetailLoading(true);
        try {
            const res = await this.service.getById(id);
            return { success: true, data: res.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setDetailLoading(false);
        }
    }

    async create(payload) {
        const store = useAssignmentTemplateStore.getState();
        store.setLoading(true);
        try {
            const res = await this.service.create(payload);
            toast.success(
                i18next.t('assignmentTemplates:messages.createSuccess', {
                    defaultValue: 'Assignment template created',
                }),
            );
            store.closeForm();
            await this.fetchAll();
            return { success: true, data: res.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, payload) {
        const store = useAssignmentTemplateStore.getState();
        store.setLoading(true);
        try {
            const res = await this.service.update(id, payload);
            toast.success(
                i18next.t('assignmentTemplates:messages.updateSuccess', {
                    defaultValue: 'Assignment template updated',
                }),
            );
            store.closeForm();
            await this.fetchAll();
            return { success: true, data: res.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * POST /assignment-templates/preview — dry-run. Errors are returned for UI (no toast; graph preview is exploratory).
     */
    async preview(body) {
        try {
            const res = await this.service.preview(body);
            return { success: true, data: res.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error);
            return { success: false, error: msg };
        }
    }

    async delete(id) {
        const store = useAssignmentTemplateStore.getState();
        store.setLoading(true);
        try {
            await this.service.delete(id);
            toast.success(
                i18next.t('assignmentTemplates:messages.deleteSuccess', {
                    defaultValue: 'Assignment template deleted',
                }),
            );
            store.closeDeleteDialog();
            await this.fetchAll();
            return { success: true };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }
}
